import dayjs from 'dayjs';
import { dayjsLocalizer } from 'react-big-calendar';

import type { IShowing } from 'src/api/landlord-showings-api';
import type { IProperty } from 'src/services/api';
import { parseDateTimeInUserTimezone } from 'src/utils/format-date-time';
import { getPropertyColor } from 'src/pages/landlord/tours/components/calendar/property-colors';

const calendarDayjs = ((value?: dayjs.ConfigType) =>
    dayjs(value)) as typeof dayjs;

calendarDayjs.extend = dayjs.extend.bind(dayjs);
calendarDayjs.localeData = () => dayjs.localeData();
calendarDayjs.min = dayjs.min.bind(dayjs);
calendarDayjs.max = dayjs.max.bind(dayjs);

// The application Day.js instance has the timezone plugin. React Big Calendar
// detects a static `.tz` method and then shifts its grid arithmetic into that
// timezone. This adapter intentionally has no `.tz`; events are converted to
// user-timezone wall clock explicitly by `toCalendarWallClockDate`.
export const tourCalendarLocalizer = dayjsLocalizer(calendarDayjs);

export interface ITourCalendarEvent {
    title: string;
    start: Date;
    end: Date;
    showing: IShowing;
}

export const buildPropertyCalendarMaps = (
    properties: IProperty[]
): {
    propertyNameById: Map<number, string>;
    propertyColorById: Map<number, string>;
} => {
    const propertyNameById = new Map<number, string>();
    const propertyColorById = new Map<number, string>();

    properties.forEach((property, i) => {
        if (!property) {
            return;
        }
        propertyNameById.set(property.id, property.name);
        propertyColorById.set(property.id, getPropertyColor(i));
    });

    return { propertyNameById, propertyColorById };
};

export const toCalendarWallClockDate = (
    isoString: string,
    timezone: string
): Date => {
    return new Date(
        parseDateTimeInUserTimezone(isoString, timezone)
            .format('YYYY-MM-DDTHH:mm:ss')
    );
};

export const getCalendarDayTimes = (
    date: Date,
    scrollHour: number
): { min: Date; max: Date; scrollToTime: Date } => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    return {
        min: new Date(year, month, day, 0, 0, 0, 0),
        max: new Date(year, month, day, 23, 59, 59, 999),
        scrollToTime: new Date(year, month, day, scrollHour, 0, 0, 0),
    };
};
